import React, { useState, useMemo, useEffect, useRef } from 'react';
import classNames from 'classnames';
import TabBar, { TabBarProps } from '@alltrails/shared/denali/components/TabBar';
import LanguageRegionCode from '@alltrails/shared/types/LanguageRegionCode';
import { defineMessages } from 'react-intl';
import useFormatMessage from '@alltrails/shared/hooks/useFormatMessage';
import * as styles from './styles/styles.module.scss';
import SeoTabbedContent from './SeoTabbedContent';

const SEO_TRAIL_TABBED_STRINGS = defineMessages({
  ADVENTURE_ANYWHERE: {
    defaultMessage: 'Adventure anywhere'
  },
  TRENDING_TRAILS: {
    defaultMessage: 'Trending trails'
  },
  PARKS_WORTH_A_LOOK: {
    defaultMessage: 'Parks worth a look'
  },
  CITIES_TO_EXPLORE: {
    defaultMessage: 'Cities to explore'
  },
  COUNTRIES_TO_CONSIDER: {
    defaultMessage: 'Countries to consider'
  }
});

type TabKey = 'COUNTRIES_TO_CONSIDER' | 'TRENDING_TRAILS' | 'PARKS_WORTH_A_LOOK' | 'CITIES_TO_EXPLORE';

export type Props = {
  languageRegionCode: LanguageRegionCode;
  displayMetric: boolean;
  data: {
    key: TabKey;
    label: string;
    data: {
      imgSrc: string;
      url: string;
      title: string;
      distance?: number;
      time?: number;
      numberOfTrails?: number;
    }[];
  }[];
};

const SeoTrailTabbed = ({ data, languageRegionCode, displayMetric }: Props): JSX.Element => {
  const [activeTab, setActiveTab] = useState(data.length ? data[0].key : '');
  const prevTab = useRef(activeTab);
  const [opacity, setOpacity] = useState(1);
  const {
    formattedDefaultMessages: { COUNTRIES_TO_CONSIDER, TRENDING_TRAILS, PARKS_WORTH_A_LOOK, CITIES_TO_EXPLORE, ADVENTURE_ANYWHERE }
  } = useFormatMessage(SEO_TRAIL_TABBED_STRINGS);
  const tabTranslations: Record<TabKey, string> = {
    COUNTRIES_TO_CONSIDER,
    TRENDING_TRAILS,
    PARKS_WORTH_A_LOOK,
    CITIES_TO_EXPLORE
  };

  const tabs: TabBarProps['tabs'] = useMemo(
    () => data.map(({ key }) => ({ id: key, label: tabTranslations[key], testId: `seo-trails-tab-${key}` })),
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [data]
  );

  useEffect(() => {
    prevTab.current = activeTab;
  }, [activeTab]);

  const handleTabChange = (id: string) => {
    if (id === activeTab) {
      return;
    }

    setOpacity(0);
    setActiveTab(id);
  };

  const activeContent = useMemo(() => {
    const getData = (key: string) => data.find(item => item.key === key)?.data;
    // we have a small fade between tabs here
    if (opacity === 0) {
      return getData(prevTab.current);
    }

    return getData(activeTab);
  }, [activeTab, data, opacity]);

  const handleTransitionEnd = () => {
    if (opacity === 0) {
      setOpacity(1);
    }
  };

  return (
    <div className={styles.container}>
      <div className={styles.title}>{ADVENTURE_ANYWHERE}</div>
      <div>
        <TabBar activeTab={activeTab} className={styles.tabBar} onChange={handleTabChange} tabs={tabs} testId="seo-trails-tab-bar" />
        <div className={classNames('xlate-none', styles.fader)} style={{ opacity }} onTransitionEnd={handleTransitionEnd}>
          <SeoTabbedContent cards={activeContent} languageRegionCode={languageRegionCode} displayMetric={displayMetric} />
        </div>
      </div>
    </div>
  );
};

export default SeoTrailTabbed;
